// apps/client/src/game/commands/tutorial.ts (M1-T19, spec §9.1) — subscribes to the store and drives the shopkeeper's first-day
// nudges: catch a fish, sell it, pay toward the move-in fee. The step lives on the server (`settings.tutorial`, moved forward only by
// `tutorial.advance`); this module only decides which nudge the current state calls for and shows it in the `dialogue` panel.
import { getEntry, t, tuning, type StringKey } from '@whiskerton/content';
import type { TownId } from '@whiskerton/shared';
import { useGame } from '../../state/store';
import { hourText } from '../../ui/screens/shop';                                       // M1-T14
import type { CommandFactory } from '../game';

export const TUTORIAL = { start: 0, fishing: 1, selling: 2, paying: 3, done: 99 } as const;
export interface Nudge { step: number; lines: StringKey[] }
/** `holdsFish`: a fish in your pockets or hand. The "sold" nudge waits until it is gone (sold) and you have bells, or you have paid:
 *  bells alone (from shells, say) would thank you for a sale you haven't made. */
export function nextNudge(s: { inTown: boolean; step: number; caughtFish: boolean; holdsFish: boolean; bells: number; loan: number; fee: number }): Nudge | null {
  if (!s.inTown || s.step >= TUTORIAL.done) return null;
  if (s.step === TUTORIAL.start) return { step: TUTORIAL.fishing, lines: ['tutorial.hello', 'tutorial.fee', 'tutorial.goFish'] };
  // Paid something already (sold and paid with the shop panels open, say): "about that loan" would ask for what isn't owed, and the
  // "paid" nudge would follow straight after it. The thanks alone closes the tutorial.
  if (s.loan < s.fee) return { step: TUTORIAL.done, lines: ['tutorial.paid', 'tutorial.bye'] };
  if (s.step === TUTORIAL.fishing && s.caughtFish) return { step: TUTORIAL.selling, lines: ['tutorial.caught', 'tutorial.goSell'] };
  if (s.step === TUTORIAL.selling && s.bells > 0 && !s.holdsFish) return { step: TUTORIAL.paying, lines: ['tutorial.sold', 'tutorial.goPay'] };
  return null;
}
export interface TutorialCommands { tutorialCheck(): void }
/** A nudge waits until these close: the shopkeeper's own panels (it would hide the sell grid, and repeat his bubble), and the catch
 *  card (the two overlapped on the first catch, M1 playtest 10-fish2-c-holdup). It also waits while a line is out or being reeled in,
 *  so it never opens before the catch card: the fish-log patch lands before the reel's ack, so the nudge used to open first, between
 *  the two, and the card then covered the speaker's name tag. */
const WAIT_FOR: readonly string[] = ['shop', 'sell', 'loan', 'catch'];
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging into GameCommands
declare module '../game' { interface GameCommands extends TutorialCommands {} }

export const commands: CommandFactory<TutorialCommands> = (game) => {
  let busy = false;
  /** The furthest nudge shown per town this page session: a failed `tutorial.advance` (offline, timeout) never re-opens the same
   *  nudge the moment it is closed; the server step catches up on the next visit. */
  let shown: { town: TownId; step: number } | null = null;
  const cmds: TutorialCommands = {
    tutorialCheck() {
      const s = useGame.getState();
      if (busy || !game.socket || !s.you || !s.town || s.screens.some((x) => x.id === 'dialogue' || WAIT_FOR.includes(x.id))) return;
      if (s.fishingPhase !== 'idle') return;                           // a line out, or a catch whose card is not open yet
      const holdsFish = s.you.items.some((i) => (i.at.loc === 'pockets' || i.at.loc === 'hand') && getEntry(i.catalogId)?.kind === 'fish');
      const nudge = nextNudge({ inTown: s.phase === 'inTown', step: s.you.settings.tutorial, caughtFish: s.you.fishLog.length > 0, holdsFish,
        bells: s.you.bells, loan: s.you.loan, fee: tuning.town.moveInFee });
      if (!nudge || (shown?.town === s.town.id && nudge.step <= shown.step)) return;
      busy = true;
      shown = { town: s.town.id, step: nudge.step };
      const params = { town: s.town.name, shopkeeper: t('shop.shopkeeperName'), fee: tuning.town.moveInFee,
        open: hourText(tuning.shop.openHour), close: hourText(tuning.shop.closeHour) };              // hours from tuning, never in text
      s.openScreen('dialogue', { speaker: t('shop.shopkeeperName'), lines: nudge.lines.map((k) => t(k, params)), kind: 'npc' });
      void game.cmd.act('tutorial.advance', { step: nudge.step }).finally(() => { busy = false; });
    },
  };
  useGame.subscribe(() => cmds.tutorialCheck());
  return cmds;
};
